import React, { useState, useEffect } from 'react';
import { Plus, MessageSquare, CheckCircle, Clock, AlertTriangle, Edit } from 'lucide-react';
import { Card, Button, Input, Select, Textarea, Table, Modal, Badge } from '../components/ui';
import { incidenciasService } from '../services/api';
import { formatDate } from '../utils/formatters';
import { TIPOS_INCIDENCIA, PRIORIDADES, ESTADOS_INCIDENCIA } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const IncidenciasPage = () => {
  const { hasPermission } = useAuth();
  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalResolver, setModalResolver] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterEstado, setFilterEstado] = useState('');
  const [solucion, setSolucion] = useState('');
  
  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    tipo: 'RECLAMO',
    prioridad: 'MEDIA',
    ubicacion: ''
  });

  useEffect(() => {
    loadData();
  }, [filterEstado]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await incidenciasService.getAll({ estado: filterEstado });
      setIncidencias(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar incidencias');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await incidenciasService.update(selected.id, form);
        toast.success('Incidencia actualizada');
      } else {
        await incidenciasService.create(form);
        toast.success('Incidencia creada');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleAtender = async (id) => {
    try {
      await incidenciasService.atender(id);
      toast.success('Incidencia en proceso');
      loadData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleResolver = async () => {
    try {
      await incidenciasService.resolver(selected.id, solucion);
      toast.success('Incidencia resuelta');
      setModalResolver(false);
      setSolucion('');
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error('Error al resolver');
    }
  };

  const handleEdit = (inc) => {
    setSelected(inc);
    setForm({
      titulo: inc.titulo,
      descripcion: inc.descripcion,
      tipo: inc.tipo,
      prioridad: inc.prioridad,
      ubicacion: inc.ubicacion || ''
    });
    setModalOpen(true);
  };

  const resetForm = () => {
    setSelected(null);
    setForm({ titulo: '', descripcion: '', tipo: 'RECLAMO', prioridad: 'MEDIA', ubicacion: '' });
  };

  const getEstadoVariant = (estado) => {
    const variants = { PENDIENTE: 'warning', EN_PROCESO: 'info', RESUELTO: 'success', CERRADO: 'default' };
    return variants[estado] || 'default';
  };

  const getPrioridadVariant = (prioridad) => {
    const variants = { ALTA: 'danger', MEDIA: 'warning', BAJA: 'info' };
    return variants[prioridad] || 'default';
  };

  const columns = [
    { 
      header: 'Incidencia',
      render: (row) => (
        <div>
          <p className="font-medium text-white">{row.titulo}</p>
          <p className="text-xs text-slate-500 truncate max-w-xs">{row.descripcion}</p>
        </div>
      )
    },
    { 
      header: 'Tipo', 
      render: (row) => <Badge variant="info">{TIPOS_INCIDENCIA.find(t => t.value === row.tipo)?.label}</Badge>
    },
    { 
      header: 'Prioridad', 
      render: (row) => <Badge variant={getPrioridadVariant(row.prioridad)}>{row.prioridad}</Badge>
    },
    { header: 'Dpto', render: (row) => row.departamento_numero || '-' },
    { header: 'Fecha', render: (row) => formatDate(row.created_at) },
    { 
      header: 'Estado', 
      render: (row) => <Badge variant={getEstadoVariant(row.estado)}>{row.estado.replace('_', ' ')}</Badge>
    },
    { 
      header: 'Acciones', 
      render: (row) => (
        <div className="flex gap-1">
          {hasPermission('ADMIN', 'PRESIDENTE', 'SECRETARIO') && (
            <>
              {row.estado === 'PENDIENTE' && (
                <Button size="xs" variant="secondary" onClick={() => handleAtender(row.id)}>Atender</Button>
              )}
              {row.estado === 'EN_PROCESO' && (
                <Button size="xs" onClick={() => { setSelected(row); setModalResolver(true); }}>Resolver</Button>
              )}
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400">
                <Edit size={14} />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  const stats = {
    pendientes: incidencias.filter(i => i.estado === 'PENDIENTE').length,
    enProceso: incidencias.filter(i => i.estado === 'EN_PROCESO').length,
    resueltas: incidencias.filter(i => i.estado === 'RESUELTO').length
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Incidencias</h1>
          <p className="text-slate-400">Reclamos y sugerencias</p>
        </div>
        <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>
          Nueva Incidencia
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="p-4 cursor-pointer hover:border-slate-600" onClick={() => setFilterEstado('')}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-700">
              <MessageSquare className="text-slate-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total</p>
              <p className="text-xl font-bold text-white">{incidencias.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 cursor-pointer hover:border-amber-500/50" onClick={() => setFilterEstado('PENDIENTE')}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <AlertTriangle className="text-amber-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Pendientes</p>
              <p className="text-xl font-bold text-amber-400">{stats.pendientes}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 cursor-pointer hover:border-blue-500/50" onClick={() => setFilterEstado('EN_PROCESO')}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Clock className="text-blue-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">En Proceso</p>
              <p className="text-xl font-bold text-blue-400">{stats.enProceso}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 cursor-pointer hover:border-emerald-500/50" onClick={() => setFilterEstado('RESUELTO')}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <CheckCircle className="text-emerald-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Resueltas</p>
              <p className="text-xl font-bold text-emerald-400">{stats.resueltas}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <Table columns={columns} data={incidencias} loading={loading} emptyMessage="No hay incidencias" />
      </Card>

      {/* Modal Nueva/Editar */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Incidencia' : 'Nueva Incidencia'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Título" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required />
          <Textarea label="Descripción" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} required />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} options={TIPOS_INCIDENCIA} required />
            <Select label="Prioridad" value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.value })} options={PRIORIDADES} required />
          </div>
          <Input label="Ubicación" value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} placeholder="Ej: Pasillo piso 3" />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Crear'}</Button>
          </div>
        </form>
      </Modal>

      {/* Modal Resolver */}
      <Modal isOpen={modalResolver} onClose={() => setModalResolver(false)} title="Resolver Incidencia">
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-slate-700/50">
            <p className="font-medium text-white">{selected?.titulo}</p>
            <p className="text-sm text-slate-400 mt-1">{selected?.descripcion}</p>
          </div>
          <Textarea 
            label="Solución" 
            value={solucion} 
            onChange={(e) => setSolucion(e.target.value)} 
            placeholder="Describa cómo se resolvió la incidencia"
            rows={4}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalResolver(false)}>Cancelar</Button>
            <Button onClick={handleResolver}>Marcar como Resuelta</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default IncidenciasPage;
